/**
 * InferenceContext — Phase 3 (PRI-291)
 *
 * Bridge context: PrimateVisionV2Inner owns the state and populates this context.
 * Child components consume via useInferenceContext() instead of receiving props.
 *
 * Phase 3 approach: "bridge" pattern — state stays in PrimateVisionV2Inner,
 * context exposes it so prop drilling is eliminated for child components.
 * Phase 5 will move the hook calls (useInference + useAnalysis) into the provider.
 */

import { createContext, useContext } from 'react'
import type { ReactNode } from 'react'
import type { PageState } from '../hooks/types'

export interface InferenceContextValue {
  pageState: PageState
  setPageStateSync: (s: PageState) => void
  jobId: string | null
  displayedJobId: string | null
  displayedResult: Record<string, unknown> | null
  analysisErrorMessage: string | null
  startAnalysis: (prompt: string) => void
  cancelAnalysis: () => void
  resetToIdle: () => void
}

const InferenceContext = createContext<InferenceContextValue | null>(null)

export function InferenceContextProvider({
  children,
  value,
}: {
  children: ReactNode
  value: InferenceContextValue
}) {
  return <InferenceContext.Provider value={value}>{children}</InferenceContext.Provider>
}

export function useInferenceContext(): InferenceContextValue {
  const ctx = useContext(InferenceContext)
  if (!ctx) throw new Error('useInferenceContext must be used inside InferenceContextProvider')
  return ctx
}

export default InferenceContext
